<script>
import allProvinces from 'provinces';
import filter from 'lodash/filter';
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import agreementValidation from '@/mixins/agreementValidation'
import { mapState } from 'vuex';

const provinces = filter(allProvinces, { country: 'CA' });

export default {
	mixins:[validationMixin,agreementValidation],
	props: {
		value: {
			type: Object,
			default() {
				return {
					street: '',
					province: '',
					city: '',
				};
			},
		},
		testid:String,
		isValidationOn:Boolean,
		disabled:Boolean,
		toolTipText:String
	},
	data() {
		return {
			provinces,
			autocomplete: null,
		};
	},
	created() {
		if (this.value) {
			if (!this.value.hasOwnProperty('street')) this.$set(this.value, 'street', '');
			if (!this.value.hasOwnProperty('city')) this.$set(this.value, 'city', '');
			if (!this.value.hasOwnProperty('province')) this.$set(this.value, 'province', undefined);
		}
	},
	validations:{
		value:{
			street:{
				required
			},
			province:{
				required
			}
		}
	},
	mounted() {
		this.initAutocomplete();
	},
	beforeDestroy() {
		if (this.autocomplete && window.google) {
			window.google.maps.event.clearInstanceListeners(this.autocomplete);
			this.autocomplete = null;
		}
	},
	computed:{
		...mapState(['currentRoleLevel']),
		isUserSeparatingParty(){
			return this.currentRoleLevel.roleAssociated === 'party'
		},
	},
	methods:{
		validateState(name) {
			const { $dirty, $error } = this.$v.value[name];
			return $dirty ? !$error : null;
		},
		initAutocomplete() {
			if (this.disabled) return;
			// If already loaded, use it directly
			if (typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.places) {
				this.attachAutocomplete();
			} else {
				const scriptId = 'google-maps-places-script';
				if (!document.getElementById(scriptId)) {
					const script = document.createElement('script');
					script.id = scriptId;
					script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
					script.async = true;
					script.defer = true;
					script.onload = () => {
						this.attachAutocomplete();
					};
					document.head.appendChild(script);
				} else {
					// wait for script to finish loading if another component injected it
					const interval = setInterval(() => {
						if (window.google && window.google.maps && window.google.maps.places) {
							clearInterval(interval);
							this.attachAutocomplete();
						}
					}, 100);
				}
			}
		},
		attachAutocomplete() {
			if (!this.$refs.streetInput || this.disabled) return;
			const inputElement = this.$refs.streetInput.$el || this.$refs.streetInput;
			if (!inputElement) return;

			this.autocomplete = new window.google.maps.places.Autocomplete(inputElement, {
				componentRestrictions: { country: 'ca' },
				fields: ['address_components', 'name'],
				types: ['address'],
			});

			this.autocomplete.addListener('place_changed', this.fillInAddress);
		},
		fillInAddress() {
			const place = this.autocomplete.getPlace();
			if (!place.address_components) return;

			let streetNumber = '';
			let route = '';
			let city = '';
			let provinceName = '';
			let provinceShort = '';

			let fallbackCity2 = '';
			let fallbackCity3 = '';
			let fallbackCityTown = '';
			let fallbackCitySub = '';

			for (const component of place.address_components) {
				const types = component.types || [];
				if (types.includes('street_number')) {
					streetNumber = component.long_name;
				}
				if (types.includes('route')) {
					route = component.long_name;
				}
				if (types.includes('locality')) {
					city = component.long_name;
				}
				if (types.includes('postal_town')) {
					fallbackCityTown = component.long_name;
				}
				if (types.includes('sublocality') || types.includes('sublocality_level_1') || types.includes('neighborhood')) {
					fallbackCitySub = component.long_name;
				}
				if (types.includes('administrative_area_level_3')) {
					fallbackCity3 = component.long_name;
				}
				if (types.includes('administrative_area_level_2')) {
					fallbackCity2 = component.long_name;
				}
				if (types.includes('administrative_area_level_1')) {
					provinceName = component.long_name;
					provinceShort = component.short_name;
				}
			}

			// Apply fallback in order of relevance to getting a city-like name
			if (!city) {
				city = fallbackCityTown || fallbackCitySub || fallbackCity3 || fallbackCity2 || '';
			}

			let finalStreet = `${streetNumber} ${route}`.trim();
			if (!finalStreet && place.name && route === '') {
				finalStreet = place.name;
			}
			
			// 1. Manually reset the DOM input to what we calculated.
			// Google maps sets it to the formatted_address by default, which we don't want.
			const inputElement = this.$refs.streetInput.$el || this.$refs.streetInput;
			if (inputElement) {
				inputElement.value = finalStreet;
			}

			// 2. Reactively set the values on the passed object
			this.$set(this.value, 'street', finalStreet);
			if (this.$v.value && this.$v.value.street) {
				this.$v.value.street.$model = finalStreet;
			}
			
			if (city) {
				this.$set(this.value, 'city', city);
			}
			
			if (provinceName) {
				const finalProvince = this.provinces.find(p => p.short === provinceShort || p.name === provinceName);
				const provToSet = finalProvince ? finalProvince.name : provinceName;
				this.$set(this.value, 'province', provToSet);
				if (this.$v.value && this.$v.value.province) {
					this.$v.value.province.$model = provToSet;
				}
			}
		},
	}
};
</script>

<template>
	<div class="app-input-group address-input">
		<b-row>
			<b-col class="label" :class="{'field-required': isValidationOn}"> Address </b-col>
		</b-row>
		<b-row no-gutters>
			<b-col cols="8" class="input col-11 col-sm-8">
				<b-form-input
					ref="streetInput"
					v-model="$v.value.street.$model"
					placeholder="ie: 123 Legal Street"
					:data-testid="testid +'-street-name'"
					:disabled="disabled"
					:state="validateState('street')"
				/>
				<div
					v-if="$v.value.street.$error"
					class="invalid-feedback d-block col-12"
				>
					This is a required field.
				</div>
			</b-col>
		</b-row>
		<b-row no-gutters>
			<b-col  class="input col-5 col-sm-4">
				<b-form-input v-model="value.city" placeholder="City" :data-testid="testid+'-city-name'" :disabled="disabled"/>
			</b-col>
			<b-col  class="input col-6 col-sm-4">
				<b-form-select
					v-model="$v.value.province.$model"
					:options="provinces"
					text-field="name"
					value-field="name"
					:data-testid="testid+'-province'"
					:disabled="disabled"
					:state="validateState('province')"
					:class="{ 'custom-disabled': disabled }"
				>
					<template #first>
						<b-form-select-option :value="undefined" disabled :data-testid="testid+'-provinces'">
							Province
						</b-form-select-option>
					</template>
				</b-form-select>
				<div
					v-if="$v.value.province.$error"
					class="invalid-feedback d-block col-12"
				>
					This is a required field.
				</div>
			</b-col>
			<b-icon-info-circle class="mt-2 ml-2"
				v-if="toolTipText && isUserSeparatingParty"
				v-b-tooltip="toolTipText">
			</b-icon-info-circle>
		</b-row>
	</div>
</template>

<style>
.pac-container {
	z-index: 10000 !important;
}
</style>
